import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartCtx = createContext(null);
const KEY = "rwiha_cart";

const snapshot = (p) => ({ name: p.name, price: p.price, image: p.images?.[0] || "", volume: p.volume || "", stock: p.stock ?? 0 });

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem(KEY) || "[]");
      return Array.isArray(v) ? v : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const add = useCallback((p, qty = 1) => {
    setItems((prev) => {
      const ex = prev.find((i) => i.product_id === p.id);
      const max = p.stock ?? 0;
      if (ex) return prev.map((i) => (i.product_id === p.id ? { ...i, ...snapshot(p), qty: Math.min(max, i.qty + qty) } : i));
      return [...prev, { product_id: p.id, qty: Math.min(max, qty), ...snapshot(p) }];
    });
  }, []);

  const setQty = useCallback((id, qty) => {
    setItems((prev) => prev.map((i) => (i.product_id === id ? { ...i, qty: Math.max(1, Math.min(i.stock || 1, qty)) } : i)));
  }, []);

  const remove = useCallback((id) => setItems((prev) => prev.filter((i) => i.product_id !== id)), []);
  const clear = useCallback(() => setItems([]), []);

  const syncWith = useCallback((products) => {
    setItems((prev) =>
      prev
        .map((i) => {
          const p = products.find((x) => x.id === i.product_id);
          if (!p || p.stock <= 0) return null;
          return { ...i, ...snapshot(p), qty: Math.min(i.qty, p.stock) };
        })
        .filter(Boolean)
    );
  }, []);

  const count = items.reduce((s, i) => s + i.qty, 0);
  const subtotal = items.reduce((s, i) => s + i.qty * i.price, 0);

  const value = useMemo(
    () => ({ items, add, setQty, remove, clear, syncWith, count, subtotal, open, setOpen }),
    [items, add, setQty, remove, clear, syncWith, count, subtotal, open]
  );
  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useCart = () => useContext(CartCtx);
